import { FC, memo } from 'react';
import clsx from 'clsx';
import { ConditionOption } from 'fragile/components/PdpConditionSelector/PdpConditionSelector';
import {
  FragileApiParts,
  SubscriptionProductUpgradeOption,
} from 'fragile/types';
import { useTranslation as t } from '@utils/hooks';
import FpsCard from '../../../components/PDP/KeySpecs/Cards/FpsCardPrebuild';
import ComponentSummary from '../ComponentSummary';

type PartsPerformanceCardProps = {
  selectedConditionOption: ConditionOption;
  selectedUpgradeOption: SubscriptionProductUpgradeOption;
  parts?: FragileApiParts;
  noContentWrapper?: boolean;
};

const PartsPerformanceCard: FC<PartsPerformanceCardProps> = ({
  selectedConditionOption,
  selectedUpgradeOption,
  parts,
  noContentWrapper,
}) => {
  const isGuaranteedNew =
    selectedConditionOption === ConditionOption.GUARANTEED_NEW;
  const summaryProds = selectedUpgradeOption.fromCms;

  // Title based on condition
  const title = isGuaranteedNew
    ? t('subscription_pdp_guaranteed_condition')
    : t('subscription_pdp_minimum_condition');

  // Description text for parts section
  const partsDescription = isGuaranteedNew
    ? t('subscription_pdp_guaranteed_description')
    : t('subscription_pdp_minimum_description');

  // Description text for performance section
  const performanceDescription = isGuaranteedNew
    ? t('subscription_pdp_avg_fps')
    : t('subscription_pdp_min_fps');

  return (
    <div
      className={clsx('w-full rounded-lg overflow-hidden bg-gray-50', {
        'p-6': !noContentWrapper,
      })}
    >
      <h2 className="text-lg font-semibold mt-4 mb-6 text-center">{title}</h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 px-4">
        {/* Parts Section */}
        <div className="border-gray-600 bg-nzxt-light-grey-100 rounded-lg p-4 mb-3">
          <div>
            <h3 className="text-lg font-semibold">Parts</h3>
            <p className="text-gray-600 text-sm mt-1 mb-5">
              {partsDescription}
            </p>

            <ComponentSummary
              products={summaryProds}
              selectedUpgradeOption={selectedUpgradeOption}
              selectedConditionOption={selectedConditionOption}
              parts={parts}
            />
          </div>
        </div>

        {/* Performance Section */}
        <div className="rounded-lg p-4">
          <div>
            <h3 className="text-lg">Performance</h3>
            <p className="text-gray-600 text-sm mt-1 mb-5">
              {performanceDescription}
            </p>

            <FpsCard
              noContentWrapper
              noHeading
              noBackground
              noDisclaimer
              noContentWrapperStyle
              noTimeSpy
              maxGames={6}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(PartsPerformanceCard);
